import { createSelector } from 'reselect';
import { matchCrews } from '../../utils';

/**
 * Determines the largest subset of offices common to all resources on a specified crew.
 *
 * @param {any} crew
 * @returns Array of common offices
 */
 function maximalOfficeSet(crew) {
  return crew.resources
    .filter(r => !!r.resource)
    .map(r => r.resource.offices)
    .reduce((prev, curr) => {
      if (!prev) {
        return curr;
      } else {
        return prev.filter(o => curr.some(o2 => o2.id === o.id));
      }
    }, null) || [];
}

/**
 * Selector for standard crews based on current filtering criteria.
 * Limits displayed standard crews to delivery or installation crews
 * depending on what is currently visible.
 *
 * Note that other standard crews may be displayed if jobs/deliveries they
 * are assigned to are visible (this happens later in eventsByCrew.js).
 */
export const standardCrewsSelector = createSelector(
  (state) => state.standardCrews.crews,
  (state) => state.settings.entity,
  (standardCrews, entity) => {
    switch (entity) {
      case 'jobs':
        return standardCrews.filter((c) => c.installationCrew);
      case 'purchaseOrders':
        return standardCrews.filter((c) => c.deliveryCrew);
      case 'bids':
      default:
        return [];
    }
  }
);

export const distinctStandardCrews = createSelector(
  standardCrewsSelector,
  (state) => state.settings.offices,
  (crews, offices) => {
    // Filter standard crews based on selected office filter
    const filteredCrews = offices!=null ? offices.length > 0
      ? crews.filter((c) => !c.office || offices.some((o) => o.id === c.office.id))
      : crews:crews;

    // Eliminate any duplicate standard crews based on equipment and resources
    // Map to include offices common to all resources to determine eligible events to be scheduled
    return filteredCrews
      .reduce((distinct, crew) => {
        if (distinct.some(c => matchCrews(c, crew))) {
          return distinct;
        } else {
          return [...distinct, crew];
        }
      }, [])
      .map(c => ({ ...c, isStandardCrew: true, offices: maximalOfficeSet(c) }));
  }
);
